/**
 * Test Harness Service
 * Frontend service for interacting with the lab integration test harness API
 */

import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

// Base URL for test harness API
const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';
const TEST_HARNESS_BASE_URL = `${API_BASE_URL}/testHarness`;

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: TEST_HARNESS_BASE_URL,
  timeout: 60000, // 60 second timeout for test operations
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth interceptor
apiClient.interceptors.request.use(async (config) => {
  try {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.warn('Failed to get auth token for test harness request');
  }
  return config;
});

/**
 * Get the current status of the lab simulator
 * @returns {Promise<Object>} Simulator status
 */
export async function getSimulatorStatus() {
  const response = await apiClient.get('/status');
  return response.data;
}

/**
 * Get available test scenarios
 * @param {string} [category] - Filter by category (normal, error, edge, timing)
 * @returns {Promise<Object>} Scenarios list
 */
export async function getScenarios(category = null) {
  const params = category ? { category } : {};
  const response = await apiClient.get('/scenarios', { params });
  return response.data;
}

/**
 * Start the lab simulator
 * @returns {Promise<Object>} Start result
 */
export async function startSimulator() {
  const response = await apiClient.post('/start');
  return response.data;
}

/**
 * Stop the lab simulator
 * @returns {Promise<Object>} Stop result
 */
export async function stopSimulator() {
  const response = await apiClient.post('/stop');
  return response.data;
}

/**
 * Reset simulator statistics and optionally clear history
 * @param {boolean} [clearHistory=false] - Clear all test history
 * @returns {Promise<Object>} Reset result
 */
export async function resetSimulator(clearHistory = false) {
  const response = await apiClient.post('/reset', { clearHistory });
  return response.data;
}

/**
 * Trigger result generation for an order
 * @param {string} orderId - Order ID to generate result for
 * @param {string} [scenario='NORMAL_LAB_RESULT'] - Scenario to use
 * @param {string} [format='HL7'] - Output format (HL7 or FHIR)
 * @returns {Promise<Object>} Generated result details
 */
export async function triggerResultGeneration(orderId, scenario = 'NORMAL_LAB_RESULT', format = 'HL7') {
  const response = await apiClient.post('/trigger', {
    orderId,
    scenario,
    format
  });
  return response.data;
}

/**
 * Run a specific test scenario
 * @param {string} scenario - Scenario type to run
 * @param {string} [format='HL7'] - Output format
 * @param {Object} [orderData] - Custom order data
 * @returns {Promise<Object>} Scenario result
 */
export async function runScenario(scenario, format = 'HL7', orderData = null) {
  const response = await apiClient.post('/run-scenario', {
    scenario,
    format,
    orderData
  });
  return response.data;
}

/**
 * Process an order message and generate result
 * @param {string} orderContent - Order content (HL7 or FHIR)
 * @param {string} [scenario] - Optional scenario to use
 * @param {string} [format] - Optional format override
 * @returns {Promise<Object>} Processing result
 */
export async function processOrder(orderContent, scenario = null, format = null) {
  const response = await apiClient.post('/process-order', {
    orderContent,
    scenario,
    format
  });
  return response.data;
}

/**
 * Upload a result to SFTP
 * @param {string} filename - Result filename
 * @param {string} content - Result content
 * @param {string} [format='HL7'] - Format of content
 * @returns {Promise<Object>} Upload result
 */
export async function uploadResult(filename, content, format = 'HL7') {
  const response = await apiClient.post('/upload-result', {
    filename,
    content,
    format
  });
  return response.data;
}

/**
 * Test SFTP connection
 * @returns {Promise<Object>} Connection test result
 */
export async function testConnection() {
  const response = await apiClient.post('/test-connection');
  return response.data;
}

/**
 * Manually poll for orders
 * @returns {Promise<Object>} Poll result with count
 */
export async function pollOrders() {
  const response = await apiClient.post('/poll-orders');
  return response.data;
}

/**
 * Get test harness activity logs
 * @param {Object} options - Query options
 * @param {number} [options.limit=50] - Maximum logs to return
 * @param {string} [options.startAfter] - Pagination cursor
 * @param {string} [options.type] - Filter by log type
 * @returns {Promise<Object>} Logs data
 */
export async function getLogs(options = {}) {
  const { limit = 50, startAfter, type } = options;
  const params = { limit };
  if (startAfter) params.startAfter = startAfter;
  if (type) params.type = type;
  
  const response = await apiClient.get('/logs', { params });
  return response.data;
}

/**
 * Get test run history
 * @param {Object} options - Query options
 * @param {number} [options.limit=20] - Maximum runs to return
 * @param {string} [options.status] - Filter by status
 * @returns {Promise<Object>} Test runs data
 */
export async function getTestRuns(options = {}) {
  const { limit = 20, status } = options;
  const params = { limit };
  if (status) params.status = status;
  
  const response = await apiClient.get('/test-runs', { params });
  return response.data;
}

/**
 * Get test harness statistics
 * @returns {Promise<Object>} Statistics data
 */
export async function getStats() {
  const response = await apiClient.get('/stats');
  return response.data;
}

/**
 * Update simulator configuration
 * @param {Object} config - Configuration updates
 * @returns {Promise<Object>} Update result
 */
export async function updateConfig(config) {
  const response = await apiClient.put('/config', config);
  return response.data;
}

/**
 * Get current stored configuration
 * @returns {Promise<Object>} Current configuration (with sensitive data masked)
 */
export async function getConfig() {
  const response = await apiClient.get('/config');
  return response.data;
}

// Export all functions as default object
const testHarnessService = {
  getSimulatorStatus,
  getScenarios,
  startSimulator,
  stopSimulator,
  resetSimulator,
  triggerResultGeneration,
  runScenario,
  processOrder,
  uploadResult,
  testConnection,
  pollOrders,
  getLogs,
  getTestRuns,
  getStats,
  updateConfig,
  getConfig
};

export default testHarnessService;
